"use client";

import ExactConverter, {
  type ExactUnit,
} from "@/components/tools/calendars/exact-converter";
import { useToolUi } from "@/components/tools/tool-ui";
import { PRESSURE_UNITS } from "@/lib/tool-kit/calendars/pressure-units";

/** English strings. A translated content file overrides these through `ui`. */
export const PRESSURE_UNIT_CONVERTER_STRINGS = {
  valueLabel: "Pressure",
  valueHint:
    "A number such as 101.325, 14.7 or 1e-3. Results are exact, using the NIST definitions of each unit.",
  fromLabel: "From",
  toLabel: "To",
  digitsLabel: "Significant digits",
  swap: "Swap units",
  resultHeading: "Result",
  plainLabel: "Converted value",
  scientificLabel: "Scientific notation",
  factorLabel: "Conversion factor",
  factorLine: "1 {from} = {factor} {to}",
  exact:
    "Exact: both units are defined by exact factors and the quotient terminates, so nothing was rounded.",
  rounded: "Rounded to {digits} significant digits.",
  tableHeading: "Your pressure in every unit",
  unitColumn: "Unit",
  valueColumn: "Value",
  enterPrompt: "Enter a pressure to convert.",
  invalidNumber:
    "That is not a number. Use digits with an optional decimal point and exponent, for example 101.325 or 1e-3.",
  copy: "Copy result",
  copied: "Copied!",
  copySummary: "{value} {from} = {result} {to}",
  unitNames: {
    pa: "pascal (Pa)",
    hpa: "hectopascal (hPa)",
    kpa: "kilopascal (kPa)",
    mpa: "megapascal (MPa)",
    bar: "bar",
    mbar: "millibar (mbar)",
    atm: "standard atmosphere (atm)",
    at: "technical atmosphere (at, kgf/cm²)",
    torr: "torr (Torr)",
    mmhg: "millimetre of mercury, conventional (mmHg)",
    inhg: "inch of mercury, conventional (inHg)",
    psi: "pound-force per square inch (psi)",
    ba: "barye (Ba, dyn/cm²)",
  },
  gaugeNote:
    "This converts a pressure from one unit to another. A gauge reading (psig, barg) is measured against the surrounding air: add the local atmospheric pressure first to get an absolute pressure.",
} as const;

export default function PressureUnitConverterWidget() {
  const strings = useToolUi(PRESSURE_UNIT_CONVERTER_STRINGS);
  const units: ExactUnit[] = PRESSURE_UNITS.map((unit) => ({
    id: unit.id,
    symbol: unit.symbol,
    factor: unit.pascals,
  }));
  return (
    <ExactConverter
      defaults={{ value: "1", from: "atm", to: "kpa" }}
      idPrefix="pu"
      notes={[strings.gaugeNote]}
      strings={strings}
      units={units}
    />
  );
}
